import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Breadcrumbs, Button, IconButton, Link, Paper, Typography } from '@mui/material';
import { ArrowLeft, Check, Lightbulb } from 'lucide-react';

import { showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import layer1Graphic from '@/assets/images/layer-1.svg';

const PLAN_FEATURES_LEFT = [
  '1 login (no ads)',
  'Manage up to 50 clients',
  '4 AI meal plans per client / month',
  'AI meal adherence and reports',
];

const PLAN_FEATURES_RIGHT = [
  'Client app, CRM and payments',
  'Manual + automated meal plans',
  'Appointments and tele-health with AI summary',
];

const featureItemTextSx = {
  color: '#23303A',
  fontSize: '1.02rem',
  lineHeight: 1.25,
  fontWeight: 500,
};

const SubscriptionPlanView: React.FC = () => {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    navigate('/profile?view=upgrade-plan');
  };

  const handleUpdatePayment = () => {
    showSuccessToast('Payment method update', 'A secure payment method update flow will be available here.');
  };

  const handleCancelSubscription = () => {
    showWarningToast('Cancellation request started', 'Please contact support to complete cancellation.');
  };

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid #dbe6eb',
          bgcolor: '#f7f9fb',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '14px',
              border: '1px solid #dbe6eb',
              bgcolor: '#f8fafc',
              px: { xs: 2, sm: 3 },
              py: 1.25,
            }}
          >
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              <Link component={RouterLink} to="/profile?view=hub" underline="hover" sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#64748b' }}>
                Settings
              </Link>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#0f172a' }}>
                Subscription Plan
              </Typography>
            </Breadcrumbs>
          </Paper>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3, lg: 3.5 }, pb: { xs: 2.5, sm: 3.5 }, pt: { xs: 2, sm: 2.5 } }}>
          <Box sx={{ mb: { xs: 2.5, sm: 2.8 }, display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
            <IconButton
              aria-label="Back to settings"
              onClick={() => navigate('/profile?view=hub')}
              sx={{
                mt: 0.3,
                width: 30,
                height: 30,
                color: '#1F2B34',
                p: 0,
                '&:hover': {
                  bgcolor: 'transparent',
                },
              }}
            >
              <ArrowLeft size={28} strokeWidth={2.1} />
            </IconButton>

            <Box sx={{ minWidth: 0, pt: 0.05 }}>
              <Typography sx={{ color: '#1F2B34', fontWeight: 700, fontSize: { xs: '1.56rem', sm: '2.02rem' }, lineHeight: 1.05 }}>
                Your CalorieScience Subscription Plan
              </Typography>
              <Typography sx={{ mt: 0.65, color: '#676B80', fontSize: { xs: '0.92rem', sm: '1.03rem' }, lineHeight: 1.2 }}>
                Manage your subscription, billing, and usage as a CalorieScience nutrition partner.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(300px, 1fr)' },
              gap: { xs: 2, lg: 2.5 },
              alignItems: 'start',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                borderRadius: '14px',
                border: '1px solid #dbe6eb',
                bgcolor: '#fff',
                overflow: 'hidden',
                boxShadow: '0 2px 6px rgba(15, 23, 42, 0.06)',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  px: { xs: 2.25, sm: 2.8 },
                  py: { xs: 2.2, sm: 2.5 },
                  background: 'linear-gradient(90deg, #22B7DE 0%, #138AB8 100%)',
                }}
              >
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'flex-end', opacity: 0.8 }}>
                  <Box component="img" src={layer1Graphic} alt="" aria-hidden sx={{ width: { xs: '42%', sm: '38%' }, height: '100%', objectFit: 'cover' }} />
                </Box>

                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1.2,
                  }}
                >
                  <Box>
                    <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: { xs: '1rem', sm: '1.06rem' }, lineHeight: 1 }}>Professional</Typography>
                    <Box
                      sx={{
                        mt: 1.35,
                        display: 'inline-flex',
                        borderRadius: '7px',
                        px: 1,
                        py: 0.4,
                        bgcolor: '#fff',
                        color: '#17A3D1',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        letterSpacing: '0.02em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Popular
                    </Box>
                    <Typography sx={{ mt: 1.05, color: 'rgba(255,255,255,0.94)', fontSize: '0.92rem' }}>Built to scale your practice faster</Typography>
                  </Box>

                  <Button
                    type="button"
                    onClick={handleUpgrade}
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      borderRadius: '10px',
                      px: 3,
                      py: 0.85,
                      minWidth: { xs: '100%', sm: 164 },
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: '#2E93C0',
                      bgcolor: '#fff',
                      boxShadow: '0 1px 2px rgba(2, 8, 23, 0.16)',
                      '&:hover': {
                        bgcolor: '#f3fbff',
                        boxShadow: '0 1px 2px rgba(2, 8, 23, 0.16)',
                      },
                    }}
                  >
                    Upgrade Plan
                  </Button>
                </Box>
              </Box>

              <Box
                sx={{
                  px: { xs: 2.25, sm: 2.8 },
                  py: { xs: 2.2, sm: 2.4 },
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  columnGap: 2.8,
                  rowGap: 1.35,
                }}
              >
                <Box sx={{ display: 'grid', gap: 1.8 }}>
                  {PLAN_FEATURES_LEFT.map((featureLabel) => (
                    <Box key={featureLabel} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.3 }}>
                      <Box
                        sx={{
                          mt: 0.23,
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          bgcolor: '#87D4F0',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Check size={12} strokeWidth={3} color="#20AEDC" />
                      </Box>
                      <Typography sx={featureItemTextSx}>{featureLabel}</Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ display: 'grid', gap: 1.8 }}>
                  {PLAN_FEATURES_RIGHT.map((featureLabel) => (
                    <Box key={featureLabel} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.3 }}>
                      <Box
                        sx={{
                          mt: 0.23,
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          bgcolor: '#87D4F0',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Check size={12} strokeWidth={3} color="#20AEDC" />
                      </Box>
                      <Typography sx={featureItemTextSx}>{featureLabel}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Paper>

            <Box sx={{ display: 'grid', gap: 1.8 }}>
              <Box
                sx={{
                  borderRadius: '10px',
                  border: '1px solid #7EC3E2',
                  bgcolor: 'transparent',
                  px: 1.5,
                  py: 1.35,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <Lightbulb size={16} color="#149BCF" style={{ marginTop: 2, flexShrink: 0 }} />
                <Typography sx={{ color: '#2A3640', fontSize: '0.92rem', lineHeight: 1.45 }}>
                  This plan defines how you use the CalorieScience platform to manage clients, create meal plans, and run your nutrition practice.
                </Typography>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: '14px',
                  border: '1px solid #dbe6eb',
                  bgcolor: '#fff',
                  px: 2.1,
                  py: 2,
                  boxShadow: '0 2px 5px rgba(15, 23, 42, 0.06)',
                }}
              >
                <Typography sx={{ color: '#1C2730', fontWeight: 700, fontSize: '0.82rem' }}>Payment</Typography>
                <Typography sx={{ mt: 0.5, mb: '80px', color: '#6E7E89', fontSize: '1.02rem', lineHeight: 1.32 }}>
                  Your next bill is for $90.00 on <Box component="span" sx={{ color: '#4E5A66', fontWeight: 700 }}>24 March 2027</Box>
                </Typography>

                <Box
                  sx={{
                    mt: 0,
                    pb: 1.7,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1.2,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: '#1B2730', fontWeight: 700, fontSize: '0.75rem', lineHeight: 1.15 }}>HSBC Business Current</Typography>
                    <Typography sx={{ mt: 0.55, color: '#7A8792', fontSize: '1.04rem' }}>Sort 40-02-XX · ***4821</Typography>
                  </Box>

                  <Button
                    type="button"
                    onClick={handleUpdatePayment}
                    variant="outlined"
                    sx={{
                      textTransform: 'none',
                      borderRadius: '10px',
                      minWidth: 90,
                      px: 2.2,
                      py: 0.75,
                      color: '#4A5864',
                      borderColor: '#D1DAE3',
                      fontWeight: 600,
                      fontSize: '1rem',
                    }}
                  >
                    Update
                  </Button>
                </Box>

                <Box sx={{ borderTop: '1px solid #E4EBF1', pt: 1.65, textAlign: 'center' }}>
                  <Button
                    type="button"
                    onClick={handleCancelSubscription}
                    variant="text"
                    sx={{
                      textTransform: 'none',
                      color: '#667380',
                      fontSize: '1.07rem',
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: 'transparent',
                        color: '#4D5863',
                      },
                    }}
                  >
                    Cancel Subscription
                  </Button>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default SubscriptionPlanView;
